import { useEffect, useRef } from "react";
import * as signalR from "@microsoft/signalr";

interface UseSignalROptions {
    url: string;
    sessionId: string;
    onConfigurationChanged: () => void;
    enabled?: boolean;
}

/**
 * Hook to connect to SignalR hub for real-time configuration updates.
 * Automatically joins the session group and listens for configuration changes.
 */
export const useSignalR = ({ url, sessionId, onConfigurationChanged, enabled = true }: UseSignalROptions) => {
    const connectionRef = useRef<signalR.HubConnection | null>(null);
    const onConfigurationChangedRef = useRef(onConfigurationChanged);

    // Keep the callback ref up to date without causing reconnections
    useEffect(() => {
        onConfigurationChangedRef.current = onConfigurationChanged;
    }, [onConfigurationChanged]);

    useEffect(() => {
        if (!enabled || !sessionId || !url) {
            console.info("SignalR: Connection skipped", { enabled, sessionId: !!sessionId, url: !!url });
            return;
        }

        // Normalize base URL (no trailing slash) so hub path is exactly /configurationHub
        const baseUrl = url.replace(/\/+$/, "");
        const hubUrl = `${baseUrl}/configurationHub`;
        console.info("SignalR: Initializing connection", { hubUrl, sessionId });

        // Create connection
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUrl)
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: retryContext => {
                    // Exponential backoff: 0s, 2s, 10s, 30s, then 30s intervals
                    if (retryContext.previousRetryCount === 0) return 0;
                    if (retryContext.previousRetryCount === 1) return 2000;
                    if (retryContext.previousRetryCount === 2) return 10000;
                    return 30000;
                },
            })
            .build();

        connectionRef.current = connection;

        // Set up event handlers
        connection.on("ConfigurationChanged", () => {
            console.info("SignalR: ConfigurationChanged event received");
            onConfigurationChangedRef.current();
        });

        connection.onreconnecting(() => {
            console.info("SignalR: Reconnecting...");
        });

        connection.onreconnected(() => {
            console.info("SignalR: Reconnected");
            // Rejoin session group after reconnection
            if (sessionId) {
                connection
                    .invoke("JoinSessionGroup", sessionId)
                    .then(() => {
                        console.info(`SignalR: Rejoined session group for ${sessionId}`);
                    })
                    .catch(err => {
                        console.error("SignalR: Failed to rejoin session group:", err);
                    });
            }
        });

        connection.onclose(error => {
            if (error) {
                console.error("SignalR: Connection closed with error:", error);
            } else {
                console.info("SignalR: Connection closed");
            }
        });

        // Start connection and join session group
        const startConnection = async () => {
            try {
                await connection.start();
                console.info("SignalR: Connected");
                await connection.invoke("JoinSessionGroup", sessionId);
                console.info(`SignalR: Joined session group for ${sessionId}`);
            } catch (err) {
                console.error("SignalR: Connection failed:", err);
            }
        };

        startConnection();

        // Cleanup on unmount
        return () => {
            const currentConnection = connectionRef.current;
            if (currentConnection && currentConnection.state !== signalR.HubConnectionState.Disconnected) {
                currentConnection
                    .invoke("LeaveSessionGroup", sessionId)
                    .then(() => {
                        return currentConnection.stop();
                    })
                    .then(() => {
                        console.info("SignalR: Connection stopped");
                    })
                    .catch(err => {
                        console.error("SignalR: Error during cleanup:", err);
                        // Force stop even if leave fails
                        currentConnection.stop().catch(() => {
                            // Ignore errors during forced stop
                        });
                    });
            }
        };
    }, [url, sessionId, enabled]);
};
